import type { GooglePlace } from "@albertine/types";
import { useQuery } from "@tanstack/react-query";
import {
    googlePlaceService,
    photoUrlOptions,
} from "../../../common/utils/google/GooglePlaceService";
import { GooglePlaceFieldsWebItem } from "../../../common/utils/google/place.util";
import config from "../../../config";

const WEB_PLACE_PHOTO_LIMIT = 4;

export function useWebPlaceDetails(
    placeId: string | undefined,
    fallbackQuery: string | undefined,
) {
    return useQuery<GooglePlace | undefined>({
        queryKey: ["webPlaceDetails", placeId, fallbackQuery],
        enabled: !!placeId || !!fallbackQuery?.trim(),
        queryFn: () => fetchWebPlaceDetails(placeId, fallbackQuery),
    });
}

async function fetchWebPlaceDetails(
    placeId: string | undefined,
    fallbackQuery: string | undefined,
): Promise<GooglePlace | undefined> {
    if (!config.mapsApiKey) {
        return undefined;
    }

    const service = googlePlaceService(config.mapsApiKey);

    const resolvedPlaceId =
        placeId ??
        (fallbackQuery ? await service.findPlaceId(fallbackQuery) : undefined);
    if (!resolvedPlaceId) {
        return undefined;
    }

    const place = await service.fetchGooglePlace(
        resolvedPlaceId,
        GooglePlaceFieldsWebItem,
    );
    if (!place) {
        return undefined;
    }

    return service.populatePlacePhotoUris(
        place,
        photoUrlOptions,
        WEB_PLACE_PHOTO_LIMIT,
    );
}
